import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { AdminLayout } from '../components/AdminLayout.js';
import { Button } from '../components/ui/button.js';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table.js';
import { requireRole } from '../lib/auth-guard.js';
import { buildPayrollCsv, categoryLabel, payrollCsvFilename } from '../lib/payroll-csv.js';
import { trpc } from '../lib/trpc.js';

export const Route = createFileRoute('/admin/payroll-export')({
  component: AdminPayrollExport,
  beforeLoad: async ({ context }) => {
    await requireRole(context.queryClient, ['ceo', 'internal_staff']);
  },
});

function currentMonth(): string {
  return new Intl.DateTimeFormat('sv-SE').format(new Date()).slice(0, 7);
}

function formatCents(cents: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
}

function AdminPayrollExport() {
  const [month, setMonth] = useState(currentMonth());
  const exportQuery = trpc.payroll.exportMonth.useQuery(
    { month },
    { enabled: /^\d{4}-\d{2}$/.test(month), retry: false },
  );

  const download = () => {
    if (!exportQuery.data) return;
    const csv = buildPayrollCsv(exportQuery.data.month, exportQuery.data.rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = payrollCsvFilename(exportQuery.data.month);
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout>
      <div className="space-y-5">
        <header>
          <h1 className="text-3xl font-bold">Lohn-Export</h1>
          <p className="max-w-4xl text-[var(--muted-foreground)]">
            Stundenliste fürs Lohnbüro: freigegebene Stundenzettel des Monats, aufgeschlüsselt
            nach Zuschlags-Kategorien. Die Lohnarten-Zuordnung macht das Lohnbüro — Beträge sind
            Info-Werte. Spaltenformat nach Abstimmung stabil halten (OPS-67).
          </p>
        </header>

        <Card>
          <CardHeader className="gap-3 md:flex-row md:items-end md:justify-between md:space-y-0">
            <div>
              <CardTitle>Monat</CardTitle>
              <CardDescription>
                Zettel zählen zum Monat ihres Periodenstarts; nur Status „freigegeben".
              </CardDescription>
            </div>
            <div className="flex items-end gap-3">
              <input
                type="month"
                aria-label="Export-Monat"
                className="h-9 rounded-md border border-[var(--border)] bg-transparent px-3 text-sm"
                value={month}
                onChange={(event) => setMonth(event.target.value)}
              />
              <Button
                type="button"
                onClick={download}
                disabled={!exportQuery.data || exportQuery.data.rows.length === 0}
              >
                CSV herunterladen
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {exportQuery.isLoading && <p className="text-sm">Berechne Stundenliste …</p>}
            {exportQuery.error && (
              <p className="text-sm text-[oklch(0.55_0.2_25)]">{exportQuery.error.message}</p>
            )}
            {exportQuery.data && (
              <>
                <p className="text-sm text-[var(--muted-foreground)]">
                  {exportQuery.data.timesheetCount} freigegebene Zettel im Monat{' '}
                  {exportQuery.data.month}.
                </p>

                {exportQuery.data.warnings.length > 0 && (
                  <div className="rounded-md border border-[oklch(0.75_0.15_85)] p-3 text-sm">
                    <strong>Hinweise (bitte vor Versand ans Lohnbüro prüfen):</strong>
                    <ul className="mt-1 list-disc pl-5">
                      {exportQuery.data.warnings.map((warning, index) => (
                        <li key={index}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Mitarbeiter</TableHead>
                      <TableHead>Kategorie</TableHead>
                      <TableHead className="text-right">Stunden</TableHead>
                      <TableHead className="text-right">Zuschlag %</TableHead>
                      <TableHead className="text-right">Betrag (Info)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {exportQuery.data.rows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-[var(--muted-foreground)]">
                          Keine freigegebenen Zettel mit Tageszeilen in diesem Monat.
                        </TableCell>
                      </TableRow>
                    )}
                    {exportQuery.data.rows.map((row, index) => (
                      <TableRow key={`${row.workerId}-${row.category}-${index}`}>
                        <TableCell className="font-medium">{row.workerName}</TableCell>
                        <TableCell>{categoryLabel(row.category)}</TableCell>
                        <TableCell className="text-right">
                          {row.hours.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-right">
                          {row.percent != null ? `${row.percent} %` : '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          {row.amountCents != null ? formatCents(row.amountCents) : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
